import { ScrapedProduct } from '@/lib/api/scraper';

export interface GroupedProduct {
  key: string;
  displayName: string;
  brand?: string;
  size?: string;
  image?: string;
  listings: {
    store: string;
    price: number;
    url?: string;
    image?: string;
    name: string;
    size?: string;
  }[];
  lowestPrice: number;
}

function normalizeForGrouping(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    // Remove common weight/size suffixes for matching
    .replace(/\b\d+\s*(g|gm|gms|kg|ml|l|ltr|litre|liter|pack|pcs|piece|pieces|unit|units)\b/gi, '')
    .trim();
}

function similarity(a: string, b: string): number {
  if (a === b) return 1;
  const setA = new Set(a.split(' '));
  const setB = new Set(b.split(' '));
  const intersection = new Set([...setA].filter(x => setB.has(x)));
  const union = new Set([...setA, ...setB]);
  return intersection.size / union.size; // Jaccard similarity
}

export function groupProducts(products: ScrapedProduct[]): GroupedProduct[] {
  const groups: GroupedProduct[] = [];

  for (const product of products) {
    const normalized = normalizeForGrouping(product.name);
    let matched = false;

    for (const group of groups) {
      const groupNormalized = normalizeForGrouping(group.displayName);
      const sim = similarity(normalized, groupNormalized);

      // Also check if one product from same store is already in group — allow multiple sizes
      const alreadyHasStore = group.listings.some(l => l.store === product.store && l.size === product.size);

      if (sim >= 0.55 && !alreadyHasStore) {
        group.listings.push({
          store: product.store,
          price: product.price,
          url: product.url,
          image: product.image,
          name: product.name,
          size: product.size,
        });
        group.lowestPrice = Math.min(group.lowestPrice, product.price);
        if (!group.image && product.image) group.image = product.image;
        matched = true;
        break;
      }
    }

    if (!matched) {
      groups.push({
        key: `${normalized}-${groups.length}`,
        displayName: product.name,
        brand: product.brand,
        size: product.size,
        image: product.image,
        listings: [{
          store: product.store,
          price: product.price,
          url: product.url,
          image: product.image,
          name: product.name,
          size: product.size,
        }],
        lowestPrice: product.price,
      });
    }
  }

  // Sort groups: multi-store groups first, then by lowest price
  groups.sort((a, b) => {
    if (b.listings.length !== a.listings.length) return b.listings.length - a.listings.length;
    return a.lowestPrice - b.lowestPrice;
  });

  return groups;
}
